import type { DuplicationStats } from '../types';

/**
 * Count how much of the traffic in `jobs` is the same scan submitted again.
 *
 * Shared by every application's stats hook — each app's history row carries a
 * `scan_id`, and no job table deduplicates it, so a resubmitted scan appears as
 * several independent rows.
 *
 * Two properties of the input bound what this can see, and the UI says so:
 * the caller passes one window's worth of history (a scan repeated on either
 * side of it looks unique), and the history endpoints only return terminal
 * rows, so a repeat still queued or processing is not counted yet.
 */
export function computeDuplicationStats(
  jobs: { scan_id?: string | null }[],
): DuplicationStats {
  const counts = new Map<string, number>();
  let submissions = 0;

  for (const job of jobs) {
    const scanId = job.scan_id?.trim();
    if (!scanId) continue;
    submissions++;
    counts.set(scanId, (counts.get(scanId) ?? 0) + 1);
  }

  const distinctScans = counts.size;
  if (distinctScans === 0) {
    return {
      submissions: 0,
      distinctScans: 0,
      duplicatedScans: 0,
      duplicatePercent: 0,
      maxSubmissions: 0,
    };
  }

  let duplicatedScans = 0;
  let maxSubmissions = 0;
  for (const count of counts.values()) {
    if (count > 1) duplicatedScans++;
    if (count > maxSubmissions) maxSubmissions = count;
  }

  return {
    submissions,
    distinctScans,
    duplicatedScans,
    duplicatePercent: (duplicatedScans / distinctScans) * 100,
    maxSubmissions,
  };
}
